import Typography from '@mui/material/Typography';
import BintempsTable from './BintempsTable';
import { getAcisData, wetbulb, estmiss, setupFilters, passesFilter } from '../js/dataAccessRoutines';

// Bin temperature Processing //

// Numerical value for missing data
const missing = -999

const setBintempsdResults = (results, setResult) => {
  if (results) {
      const code = (
        <BintempsTable 
          tabledata={results.tabledata}
          estcnt={results.estcnt}
          misscnt={results.misscnt}
          totobscnt={results.totobscnt}
          userParams={results.userParams}
          stnName={results.stnName}
        />
      );
      setResult(code)
  } else {
    setResult("")
  }
}

// Summarize data as needed for output table
const summarizeBintemps = (bincnt, wetsum, wetcnt, lobin, hibin, binsize) => {
  var mwetb, lotemp, hitemp, i, totobscnt= 0, results = [];
  for (let lb = hibin; lb >= lobin; lb-=binsize) {
    i = (lb - lobin) / binsize
    mwetb = wetcnt[i] > 0 ? Math.round((wetsum[i] / wetcnt[i])*10)/10 : missing
    lotemp = lb
    hitemp = lotemp + binsize-1
    totobscnt += bincnt[i]
    results.push({
      index: i,
      range: lotemp+" to "+hitemp,
      count: bincnt[i], 
      mwetb: mwetb !== missing ? mwetb.toFixed(1) : "-"
    })
  }
  return [results, totobscnt]
}

// Assign bin and do wet bulb summarizations
const getBin = (temp, wetb, binsize, lowest_bin, bincnt, wetsum, wetcnt) => {
    const tbin = Math.floor((temp - lowest_bin) / binsize)
    bincnt[tbin] += 1
    if (wetb != missing) {
      wetsum[tbin] += wetb
      wetcnt[tbin] += 1
    }
  return [bincnt, wetsum, wetcnt]
}

const processBintempData = (res, userParams) => {
    var temp, dwpt, rhum, wetb, minTemp = 999, maxTemp = -999, sresdata = [];
    const stnName = res.meta.name + ", " + res.meta.state
    const filters = setupFilters(userParams)

    // extract and format hourly data passing filters
    res.data.forEach((results) => {
      temp = results[1] != "M" ? parseFloat(results[1]) : missing
      dwpt = results[3] != "M" ? parseFloat(results[3]) : missing
      rhum = results[4] != "M" ? parseFloat(results[4]) : missing
      wetb = results[2] != "M" ? parseFloat(results[2]) : wetbulb(temp, dwpt, rhum, missing)
      // need all values to do estimation, if requested
      sresdata.push([results[0], temp, wetb])
      // get max and min just of values passing filters
      if (passesFilter(results[0], filters)) {
         if (temp !== missing) {
          if (temp < minTemp) {
            minTemp = temp
          }
          if (temp > maxTemp) {
            maxTemp = temp
          }
        }
      }
    })

    //table initializations
    const lowest_bin = Math.floor(minTemp / 10) * 10;
    const highest_bin = (Math.floor((maxTemp - lowest_bin) / userParams.binsize) * userParams.binsize) + lowest_bin;
    const number_of_bins = ((highest_bin - lowest_bin)/ userParams.binsize) + 1;
    let bincnt = Array(number_of_bins).fill(0)
    let wetsum = Array(number_of_bins).fill(0)
    let wetcnt = Array(number_of_bins).fill(0)
    var misscnt = 0
    var estcnt = 0

    //fill bin temperature array and coincident web bulbs
    sresdata.forEach((result, i) => {
      let [resDate, temp, wetb] = result
      if (passesFilter(resDate, filters)) {
        // copy to new variable so estimates don't get used to fill subsequent values
        var tempv = temp
        var wetbv = wetb
        // estimate missing temperature, if requested
        if (tempv == missing && userParams.moreOptions.estimation) {
          tempv = Math.round(estmiss(sresdata, i, 1)) //temperature item 1 in array
          if (tempv !== missing) {
            estcnt += 1
          }
        }
        // ... same for wet bulb
        if (wetbv === missing && userParams.moreOptions.estimation) {
          wetbv = Math.round(estmiss(sresdata, i, 2)) //wet bulb is item 2 in array
        }
        if (tempv != missing) {
          [bincnt, wetsum, wetcnt] = getBin(tempv, wetbv, userParams.binsize, lowest_bin, bincnt, wetsum, wetcnt);
        } else {
          misscnt += 1
        }
      }
    })
    // do final calculations and consolidate for output
    const [tabledata, totobscnt] = summarizeBintemps(bincnt, wetsum, wetcnt, lowest_bin, highest_bin, userParams.binsize)
    return {tabledata, estcnt, misscnt, totobscnt, userParams, stnName}
}

export function processBintemps(userParams, setResult, hourlyVx) {
    const sid = userParams.sid;
    const network = sid.indexOf(" ") > 0 ? sid.split(" ")[1] : 'icao';
    const sdate = [userParams.fromDate.year(),userParams.fromDate.month()+1,userParams.fromDate.date(),1] || [2023,12,1,1];
    // end hour 24, which is hour 0 the next day
    const nextday = new Date(userParams.toDate);
    nextday.setDate(userParams.toDate.date() + 1)
    const edate = [nextday.getFullYear(),nextday.getMonth()+1,nextday.getDate(),0] || [2024,1,1,0];
    const acisParams = {
        sid: sid,
        sdate: sdate,
        edate: edate,
        elems: [
          {vX: hourlyVx[network].temp, interval: [0,0,0,1], duration: 1},
          {vX: hourlyVx[network].wetb, interval: [0,0,0,1], duration: 1},
          {vX: hourlyVx[network].dwpt, interval: [0,0,0,1], duration: 1},
          {vX: hourlyVx[network].rhum, interval: [0,0,0,1], duration: 1},
        ]
    }
    getAcisData(acisParams)
    .then(res => {
      const btresults = processBintempData(res, userParams);
      setBintempsdResults(btresults, setResult)
    })
    .catch(error =>  { 
      console.log("Data retrieval error: " ); 
      console.log(error)
      setResult((
        <Typography sx={{color: "red"}}>
          Error processing request - {error}
        </Typography>
      ))
    })
}